import type { Property } from "@/types/property";

export type PropertyReferrerKey =
  | "properties"
  | "managed-properties"
  | "deals"
  | "home"
  | "realtor"
  | "developer";

export interface PropertyReferrer {
  key: PropertyReferrerKey;
  /** Profile slug/id — only used for realtor/developer referrers. */
  id?: string;
}

export interface ReferrerCrumb {
  label: string;
  href: string;
}

const STATIC_CRUMBS: Record<string, ReferrerCrumb> = {
  properties: { label: "Properties", href: "/properties" },
  "managed-properties": {
    label: "Managed Properties",
    href: "/managed-properties",
  },
  deals: { label: "Deals", href: "/weekly-deals" },
};

export const DEFAULT_REFERRER_CRUMB: ReferrerCrumb =
  STATIC_CRUMBS.properties;

const realtorDisplayName = (property?: Property | null): string => {
  const realtor = property?.realtor;
  if (!realtor) return "Realtor";
  return (
    realtor.business_name ||
    [realtor.first_name, realtor.last_name].filter(Boolean).join(" ") ||
    "Realtor"
  );
};

/**
 * Builds a property details URL that remembers where the user navigated
 * from, so the details page breadcrumb can reflect the actual referrer
 * instead of always showing "Properties". When a realtor slug/id is known,
 * the URL is nested under that realtor's profile (/realtors/[slug]/[id])
 * instead of the flat /property/[id] path.
 */
export function buildPropertyDetailsUrl(
  propertyId: string,
  referrer?: PropertyReferrer,
  realtorSlugOrId?: string | null,
): string {
  const base = realtorSlugOrId
    ? `/realtors/${realtorSlugOrId}/${propertyId}`
    : `/property/${propertyId}`;
  if (!referrer) return base;
  const params = new URLSearchParams({ from: referrer.key });
  if (referrer.id) params.set("fromId", referrer.id);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/**
 * Resolves the middle breadcrumb crumb from the `from`/`fromId` query params.
 * Returns null when no middle crumb should render (home referrer) and falls
 * back to the Properties crumb for unknown/direct visits so shared links
 * keep working.
 */
export function resolveReferrerCrumb(
  from: unknown,
  fromId: unknown,
  property?: Property | null,
): ReferrerCrumb | null {
  if (from === "home") return null;

  if (typeof from === "string" && STATIC_CRUMBS[from]) {
    return STATIC_CRUMBS[from];
  }

  if (from === "realtor") {
    const id =
      typeof fromId === "string" && fromId
        ? fromId
        : property?.realtor?.slug || property?.realtor?.id;
    if (!id) return DEFAULT_REFERRER_CRUMB;
    return { label: realtorDisplayName(property), href: `/realtors/${id}` };
  }

  if (from === "developer") {
    const createdBy = property?.createdBy;
    const isDeveloper = createdBy?.role === "developer";
    const id =
      typeof fromId === "string" && fromId
        ? fromId
        : isDeveloper
          ? createdBy.slug || createdBy.id
          : undefined;
    return {
      label:
        isDeveloper && createdBy.full_name ? createdBy.full_name : "Developer",
      href: id ? `/developers/${id}` : "/developers",
    };
  }

  return DEFAULT_REFERRER_CRUMB;
}
